export const GSI_CLIENT_SRC = 'https://accounts.google.com/gsi/client'
export const GSI_AFTER_LCP_MS = 1500
/** If LCP never fires (hidden tab, no observer entry), still load GSI. */
export const GSI_LCP_FALLBACK_MS = 8000

type LoadGsiOptions = {
  immediate?: boolean
}

let loadPromise: Promise<void> | null = null
let injectNow: (() => void) | null = null

function alreadyInDocument (): boolean {
  return !!document.querySelector(`script[src="${GSI_CLIENT_SRC}"]`)
}

function injectScript (resolve: () => void, reject: (error: Error) => void): void {
  if (alreadyInDocument()) {
    resolve()
    return
  }

  const script = document.createElement('script')
  script.src = GSI_CLIENT_SRC
  script.async = true
  script.onload = () => resolve()
  script.onerror = () => {
    const error = new Error('GSI script failed to load')
    // eslint-disable-next-line no-console
    console.error(error.message)
    reject(error)
  }
  document.head.appendChild(script)
}

/**
 * Load Google Identity Services off the critical path (TR-40769).
 * Default: after LCP + 1.5s. `immediate` injects now (guest-login modal).
 */
export function loadGsiClient (options: LoadGsiOptions = {}): Promise<void> {
  if (typeof document === 'undefined') {
    return Promise.resolve()
  }

  if (alreadyInDocument()) {
    return Promise.resolve()
  }

  if (options.immediate && injectNow) {
    injectNow()
  }

  if (loadPromise) {
    return loadPromise
  }

  loadPromise = new Promise((resolve, reject) => {
    let started = false
    const startInject = () => {
      if (started) {
        return
      }
      started = true
      injectNow = null
      injectScript(resolve, reject)
    }

    if (options.immediate) {
      startInject()
      return
    }

    injectNow = startInject

    const afterLcp = () => {
      window.setTimeout(startInject, GSI_AFTER_LCP_MS)
    }

    if (typeof PerformanceObserver === 'undefined') {
      afterLcp()
      return
    }

    try {
      const observer = new PerformanceObserver((list, obs) => {
        if (list.getEntries().length) {
          obs.disconnect()
          afterLcp()
        }
      })
      observer.observe({ type: 'largest-contentful-paint', buffered: true })
      window.setTimeout(() => {
        observer.disconnect()
        afterLcp()
      }, GSI_LCP_FALLBACK_MS)
    } catch {
      afterLcp()
    }
  })

  return loadPromise
}

export function resetLoadGsiClientForTests (): void {
  loadPromise = null
  injectNow = null
}
